import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { resource_category_label } from '../../../../lib/resource_centre/resources';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { BodyText } from '../../typography/PrimaryTypography';
import { Box } from '@mui/material';

interface CategorySelectProps {
  handleChangeCategories: (resource_category: Array<string> | string | null) => void;
  options?: Array<string>;
  selected_options?: Array<string> | string | null;
  disabled?: boolean;
  input_label?: string;
  clearable?: boolean;
  multi_select?: boolean;
  tag_limit?: number;
  searchable?: boolean;
  color?: string;
  disable_underline?: boolean;
  disable_selected_tags?: boolean;
}

const default_resource_category_options: Array<string> = [
  'Platform Training',
  'Standards and Frameworks',
  'Programs and Initiatives',
  'Technical Training',
  'Other'
];

/**
 * Resource Category selection auto-complete component that handles a variety of built-in scenarios.
 * @param {string | null} props.handleChangeCategories Callback function to notify the parent component when the selected options have changed.
 * @param {Array<string>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {string | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {string} props.color A custom color used for the input component of the autocomplete. Defaults to standard MUI color.
 * @param {boolean} props.disable_underline Allows the hiding of the underline on a standard variant text field.
 * @param {boolean} props.disable_selected_tags Optionally replace the selected tags with text when autocomplete is expanded to render input at a predictable height.
 * @returns {JSX.Element}
 */
const ResourceCategorySelect = (props: CategorySelectProps) => {
  const [optionList, setOptions] = React.useState<Array<string>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<Array<string> | string | null>(
    props.multi_select ? [] : null
  );

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<string> = props.options
        ? // Options supplied by the parent component.
          props.options
        : default_resource_category_options;
      setOptions(options);
    })();
  }, [props.options]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  return (
    <Autocomplete
      id="resource-category-select"
      fullWidth
      multiple={props.multi_select}
      limitTags={props.tag_limit}
      disableCloseOnSelect={props.multi_select}
      disabled={props.disabled}
      disableClearable={props.clearable === false}
      options={optionList}
      value={selectedOptions}
      getOptionLabel={(option: string) => {
        return option;
      }}
      size="small"
      isOptionEqualToValue={(option: string, value: string) => option === value}
      noOptionsText={`No ${resource_category_label.many.toLowerCase()}`}
      renderValue={
        !props.multi_select || !props.disable_selected_tags
          ? undefined
          : (value: string | Array<string>) => {
              return Array.isArray(value) && value.length > 0
                ? `${value.length} ${value.length === 1 ? resource_category_label.one : resource_category_label.many}`
                : undefined;
            }
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <CategoryOutlinedIcon />
              <BodyText>{props.input_label ?? resource_category_label.one}</BodyText>
            </Box>
          }
          variant="standard"
          inputProps={{ ...params.inputProps, readOnly: props.searchable === false }}
          InputProps={{ ...params.InputProps, disableUnderline: props.disable_underline }}
          sx={{
            input: { color: props.color ? props.color : 'currentcolor' },
            '& .MuiSvgIcon-root': {
              color: props.color ? props.color : 'currentcolor'
            }
          }}
        />
      )}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: Array<string> | string | null
      ) => {
        setSelectedOptions(value);
        props.handleChangeCategories(value);
      }}
    />
  );
};

export default ResourceCategorySelect;
